// Feature #257: thin API client for email template CRUD. Lives alongside
// the existing tap_email_templates_router endpoints (the same router was
// extended with create/update/delete to keep the URL surface tight).
//
// Note the response shape: ``EmailTemplateResponse`` from the backend's
// tap_email_message schema is the *projection* the compose picker sees —
// it deliberately omits ``body_text`` and the timestamps. The management
// UI doesn't need them today; if a future feature wants to surface the
// plain-text body the backend should swap in the richer
// ``lab_email_template.EmailTemplateResponse`` schema rather than have
// the client stitch the missing fields together.

import client from './client'

export interface EmailTemplate {
  id: string
  key: string
  name: string
  subject: string
  bodyHtml: string
  availableMergeFields: string[] | null
  isActive: boolean
}

interface RawEmailTemplate {
  id: string
  key: string
  name: string
  subject: string
  body_html: string
  available_merge_fields?: string[] | null
  is_active: boolean
}

const mapTemplate = (raw: RawEmailTemplate): EmailTemplate => ({
  id: raw.id,
  key: raw.key,
  name: raw.name,
  subject: raw.subject,
  bodyHtml: raw.body_html,
  availableMergeFields: raw.available_merge_fields ?? null,
  isActive: raw.is_active,
})

export interface EmailTemplateInput {
  key: string
  name: string
  subject: string
  bodyHtml: string
  bodyText?: string | null
  availableMergeFields?: string[] | null
}

const toCreatePayload = (input: EmailTemplateInput) => ({
  key: input.key,
  name: input.name,
  subject: input.subject,
  body_html: input.bodyHtml,
  body_text: input.bodyText ?? null,
  available_merge_fields: input.availableMergeFields ?? null,
})

const toUpdatePayload = (input: Partial<EmailTemplateInput>) => {
  const out: Record<string, unknown> = {}
  if (input.key !== undefined) out.key = input.key
  if (input.name !== undefined) out.name = input.name
  if (input.subject !== undefined) out.subject = input.subject
  if (input.bodyHtml !== undefined) out.body_html = input.bodyHtml
  if (input.bodyText !== undefined) out.body_text = input.bodyText
  if (input.availableMergeFields !== undefined) {
    out.available_merge_fields = input.availableMergeFields
  }
  return out
}

export const listEmailTemplates = async (
  includeInactive: boolean = false,
): Promise<EmailTemplate[]> => {
  const response = await client.get<RawEmailTemplate[]>('/tap/email-templates', {
    params: { include_inactive: includeInactive },
  })
  return (response.data || []).map(mapTemplate)
}

export const getEmailTemplate = async (id: string): Promise<EmailTemplate> => {
  const response = await client.get<RawEmailTemplate>(`/tap/email-templates/${id}`)
  return mapTemplate(response.data)
}

export const createEmailTemplate = async (
  input: EmailTemplateInput,
): Promise<EmailTemplate> => {
  const response = await client.post<RawEmailTemplate>(
    '/tap/email-templates',
    toCreatePayload(input),
  )
  return mapTemplate(response.data)
}

export const updateEmailTemplate = async (
  id: string,
  input: Partial<EmailTemplateInput>,
): Promise<EmailTemplate> => {
  const response = await client.patch<RawEmailTemplate>(
    `/tap/email-templates/${id}`,
    toUpdatePayload(input),
  )
  return mapTemplate(response.data)
}

export const deactivateEmailTemplate = async (id: string): Promise<void> => {
  await client.delete(`/tap/email-templates/${id}`)
}
